import React, {useRef, useState} from "react";
import {Box, ButtonBase, Popover, Typography} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";

// Normalises a swatch entry (string OR {main, dark}) to the colour displayed in the grid.
const swatchColor = (entry) => (typeof entry === "string" ? entry : entry?.main);

const ColorPicker = ({label, value, colors, onChange, swatchSize = 32, columns = 4}) => {
    const [anchorEl, setAnchorEl] = useState(null);
    const triggerRef = useRef(null);

    const safeIndex = Number.isFinite(value) && value >= 1 && value <= colors.length ? value : 1;
    const currentColor = swatchColor(colors[safeIndex - 1]);

    const handleOpen = () => setAnchorEl(triggerRef.current);
    const handleClose = () => setAnchorEl(null);
    const handlePick = (idx) => {
        onChange(idx);
        handleClose();
    };

    return (
        <Box>
            {label && (
                <Typography variant="subtitle2" color="text.secondary" sx={{mb: 1}}>
                    {label}
                </Typography>
            )}
            <ButtonBase
                ref={triggerRef}
                onClick={handleOpen}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: 1.5,
                    py: 1,
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    width: "100%",
                    justifyContent: "flex-start",
                }}
            >
                <Box
                    sx={{
                        width: swatchSize,
                        height: swatchSize,
                        backgroundColor: currentColor,
                        borderRadius: 0.5,
                        border: 1,
                        borderColor: "divider",
                    }}
                />
                <Typography variant="body2" sx={{flex: 1, textAlign: "left"}}>
                    {currentColor}
                </Typography>
                <ArrowDropDownIcon />
            </ButtonBase>

            <Popover
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{vertical: "bottom", horizontal: "left"}}
                transformOrigin={{vertical: "top", horizontal: "left"}}
                slotProps={{paper: {sx: {p: 1}}}}
            >
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: `repeat(${columns}, ${swatchSize + 8}px)`,
                        gap: 0.5,
                    }}
                >
                    {colors.map((entry, i) => {
                        const idx = i + 1;
                        const color = swatchColor(entry);
                        const isSelected = idx === safeIndex;
                        return (
                            <ButtonBase
                                key={idx}
                                onClick={() => handlePick(idx)}
                                aria-label={`Color ${idx} ${color}`}
                                sx={{
                                    width: swatchSize + 8,
                                    height: swatchSize + 8,
                                    p: 0.5,
                                    borderRadius: 0.5,
                                    border: 2,
                                    borderColor: isSelected ? "primary.main" : "transparent",
                                    "&:hover": {
                                        borderColor: isSelected ? "primary.main" : "action.hover",
                                        boxShadow: 2,
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        width: "100%",
                                        height: "100%",
                                        backgroundColor: color,
                                        borderRadius: 0.5,
                                    }}
                                />
                            </ButtonBase>
                        );
                    })}
                </Box>
            </Popover>
        </Box>
    );
};

export default ColorPicker;
